import { ID } from '@griply/types';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getExternalConnectionCalendars } from 'shared/services/backend-api';

import { queryKey } from './use-external-connection-calendars-query';

export type { ConnectionCalendar } from '@griply/types';

export type UseExternalConnectionsCalendarsQueriesOptions = {
  enabled?: boolean;
};

export const useExternalConnectionsCalendarsQueries = (
  connectionIds: ID[],
  { enabled = true }: UseExternalConnectionsCalendarsQueriesOptions = {},
) => {
  const queries = useQueries({
    queries: connectionIds.map((id) => ({
      queryKey: [queryKey, id],
      queryFn: () => getExternalConnectionCalendars(id),
      // keeps these out of the pending state that, under the global suspense default,
      // suspends the root-mounted EventFormProvider and blanks the app shell
      placeholderData: [],
      staleTime: 5 * 60 * 1000,
      enabled,
    })),
  });

  const calendars = useMemo(
    () =>
      queries.flatMap((query, index) =>
        (query.data ?? []).map((calendar) => ({
          ...calendar,
          connectionId: connectionIds[index],
        })),
      ),
    [queries, connectionIds],
  );

  return {
    calendars,
    isPending: queries.some((query) => query.isPlaceholderData),
  };
};
